import { brazilianRealFormat } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';
import { Box, ButtonIcon, Flex, Hr, Icons, RichContent, Section, Typography } from 'components';
import { IconProps } from 'components/icons';
import { VideoProviderEnum } from 'features/contents';
import { JourneyEnrollment, useJourneys } from 'features/journeys';
import { ProductJourneyRelationalMetricFormattingEnum } from 'features/journeys/types/ProductJourneyRelationalMetricFormattingEnum';

import { useProducts } from 'features/products';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const Degradee = styled(Flex)`
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(${(props: any) => props['data-degradee']}, 0.5) 70%,
    rgba(${(props: any) => props['data-degradee']}, 1) 100%
  );
`;

interface ProductHeroProps {
  hasNextItems?: boolean;
  openStartDateModal: () => void;
  openRelationalMetricModal: (relationalMetricUuid: string, period?: number) => void;
  fetchJourney: (forceEnroll: boolean) => void;
}

const BoxIcon = styled(Icons)<IconProps>`
  width: ${({ icon }) => (icon === 'currencyUsd' ? '28px' : '24px')};
  color: ${({ theme }) => theme.colors.white25};
`;

const ProductHero = ({ hasNextItems = false, openStartDateModal, openRelationalMetricModal, fetchJourney }: ProductHeroProps) => {
  const { productSlug } = useParams();

  const { productDetails } = useProducts();
  const { setJourneyVideoPlayer } = useJourneys();

  const mainRelationalMetric = productDetails?.journey?.relationalMetrics?.find(
    (relationalMetric) => relationalMetric?.uuid === productDetails?.journey?.mainRelationalMetricUuid
  );

  const showPlayerButton = useMemo(() => {
    if (productDetails?.journey?.videoProvider && productDetails?.journey?.externalVideoProviderIdOrUrl) {
      if (productDetails?.journey?.videoProvider === VideoProviderEnum.ProtectedVideo && !productDetails?.journey?.protectedVideoOtp) {
        return false;
      }

      if (productDetails?.journey?.videoProvider === VideoProviderEnum.Panda && !productDetails?.journey?.protectedVideoPlaybackInfo) {
        return false;
      }

      return true;
    }

    return false;
  }, [productDetails]);

  return (
    <Degradee data-degradee={hasNextItems ? '15, 15, 15' : '23, 25, 24'} flexDirection="column" flexGrow={0}>
      <Section flexGrow={0} justifyContent="space-between" pt={{ m: 6, d: 380 }} pb={{ d: 200 }}>
        <Flex flexGrow={0} flexDirection="column" maxWidth={{ d: 685 }}>
          {!!productDetails?.product.hat && (
            <Typography type="type3" color="gray_500" upper mb={2}>
              {productDetails?.product.hat}
            </Typography>
          )}

          <Typography type="type6" color="white" fontWeight="bold">
            {productDetails?.product.name}
          </Typography>

          {!!productDetails?.product.description && (
            <Box mt={{ m: 3, d: 4 }}>
              <RichContent defaultType="type1" currentTheme="dark">
                {productDetails?.product.description}
              </RichContent>
            </Box>
          )}
        </Flex>

        {!!productDetails?.journey && (
          <>
            <Flex flexGrow={0} alignItems="flex-start" mt={{ m: 5, d: 0 }} width={{ m: '100%', d: 370 }}>
              <Flex bg="dark_350" flexDirection="column" borderRadius={4}>
                {(!!productDetails?.journey?.externalVideoProviderIdOrUrl ||
                  !!productDetails?.journey?.videoTitle ||
                  !!productDetails?.journey?.videoDescription) && (
                  <>
                    <Flex flexGrow={1} flexDirection="column" alignItems="flex-start" p={3}>
                      {showPlayerButton && (
                        <ButtonIcon
                          icon="play"
                          iconWidth={26}
                          width={50}
                          height={50}
                          borderRadius="100%"
                          color="white"
                          mb={3}
                          onClick={() => {
                            setJourneyVideoPlayer({
                              videoProvider: productDetails?.journey?.videoProvider,
                              externalVideoProviderIdOrUrl: productDetails?.journey?.externalVideoProviderIdOrUrl,
                              protectedVideoOtp: productDetails?.journey?.protectedVideoOtp,
                              protectedVideoPlaybackInfo: productDetails?.journey?.protectedVideoPlaybackInfo,
                              protectedVideoPlayerTheme: productDetails?.journey?.protectedVideoPlayerTheme,
                            });
                          }}
                        />
                      )}

                      {!!productDetails?.journey?.videoTitle && (
                        <Typography color="primary_400" fontWeight="bold" mb={2}>
                          {productDetails?.journey?.videoTitle}
                        </Typography>
                      )}

                      {!!productDetails?.journey?.videoDescription && (
                        <Typography type="type0" color="gray_500">
                          {productDetails?.journey?.videoDescription}
                        </Typography>
                      )}
                    </Flex>

                    <Hr m={0} color="dark_150" />
                  </>
                )}

                {productDetails?.enrolled ? (
                  <>
                    {!!productDetails?.journey?.showJourneyStartDate && (
                      <Flex flexWrap="nowrap">
                        <Flex flexGrow={0} justifyContent="center" width={{ m: 60, d: 70 }}>
                          <BoxIcon icon="calendar" />
                        </Flex>

                        <Flex flexDirection="column" justifyContent="center" flexGrow={1} py={4}>
                          <Typography type="type1" color="gray_500" upper>
                            Início da sua jornada
                          </Typography>

                          <Typography type="type4" color="white" fontWeight="bold" mt={1}>
                            {formatDateFromUtcToTimezone(productDetails?.journey?.journeyStartDate).format('DD/MM/YYYY')}
                          </Typography>
                        </Flex>

                        {productDetails?.journey?.allowChangeJourneyStartDate && (
                          <Flex flexGrow={0} px={{ m: 3, d: 4 }}>
                            <ButtonIcon onClick={openStartDateModal} icon="edit" bg="transparent" color="primary_400" colorHover="primary_200" />
                          </Flex>
                        )}
                      </Flex>
                    )}

                    {!!mainRelationalMetric && (
                      <>
                        <Hr m={0} color="dark_150" />

                        <Flex flexWrap="nowrap">
                          <Flex flexGrow={0} justifyContent="center" width={{ m: 60, d: 70 }}>
                            <BoxIcon icon="currencyUsd" />
                          </Flex>

                          <Flex flexDirection="column" justifyContent="center" flexGrow={1} py={4}>
                            <Typography type="type1" color="gray_500" upper>
                              {mainRelationalMetric?.assessmentValueLegendText}
                            </Typography>

                            <Typography type="type4" color="white" fontWeight="bold" mt={1}>
                              {mainRelationalMetric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                                ? brazilianRealFormat(mainRelationalMetric?.periodValue || 0)
                                : mainRelationalMetric?.periodValue}
                            </Typography>
                          </Flex>

                          {productDetails?.journey?.allowEditLatestRelationalMetricAssessment && (
                            <Flex flexGrow={0} px={{ m: 3, d: 4 }}>
                              <ButtonIcon
                                onClick={mainRelationalMetric ? () => openRelationalMetricModal(mainRelationalMetric?.uuid) : undefined}
                                icon="edit"
                                bg="transparent"
                                color="primary_400"
                                colorHover="primary_200"
                              />
                            </Flex>
                          )}
                        </Flex>
                      </>
                    )}
                  </>
                ) : (
                  <JourneyEnrollment productSlug={productSlug} fetchJourney={fetchJourney} />
                )}
              </Flex>
            </Flex>
          </>
        )}
      </Section>
    </Degradee>
  );
};

export default ProductHero;
